import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { ContextualMap } from "../ContextualMap";
import { createPart3OperationId, requestPart3LaunchLocation } from "../../lib/part3LaunchLocation";
import { nextPart3ArrivalDwell } from "../../lib/tier3Arrival";
import { captureKarenPart3Conversation } from "../../utils/karenPart3Conversation";
import { formatConversationBudget } from "../../lib/parkedConversation.mjs";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const NAV = new Set(["to_pupil", "to_centre", "circuit"]);
const CONVERSATIONS = new Set(["pickup_briefing", "centre_briefing", "debrief"]);
function centreConversationNotice(phase, route) {
  // The pickup transfer is outside the centre clock; showing that system detail over the live map obscures driving guidance.
  if (phase === "to_pupil") return "";
  if (route?.near_centre_for_conversation !== true) return route?.message || "";
  if (phase === "to_centre") return "Within 200 yards of Fengate. When safely parked, open the second briefing.";
  if (phase === "circuit" && route?.session?.circuit_departed === true)
    return "Within 200 yards of Fengate on your return. When safely parked, open the final debrief.";
  return route?.message || "";
}

const TERMINAL = new Set(["finished", "cancelled"]);
const EMPTY = Object.freeze([]);
const COMMANDS = new Set(["get_time", "repeat_roundabout", "shorten_route", "return_to_centre", "continue_part3", "finish_part3", "cancel_part3"]);
const PHASES = {
  to_pupil: "Collect your pupil", pickup_briefing: "First briefing · pupil pickup",
  to_centre: "Journey to Fengate", centre_briefing: "Second briefing · Fengate",
  circuit: "Part 3 roundabout circuit", debrief: "One final debrief · Fengate",
  finished: "Part 3 finished at Fengate", cancelled: "Part 3 cancelled",
};
const errorMessage = error => {
  const detail = error?.response?.data?.detail;
  return (typeof detail === "string" ? detail : detail?.message) || error?.message || "This Part 3 request could not be confirmed. Please retry.";
};
const sessionScope = session => `${session?.launch_id}:${session?.phase}:${session?.phase_version}`;
const minutes = value => {
  const seconds = Math.max(0, Math.ceil(value / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

export function validPart3Session(value, launchId) {
  return Boolean(value && value.launch_id === launchId && Object.hasOwn(PHASES, value.phase)
    && Number.isInteger(value.phase_version) && value.phase_version > 0
    && typeof value.student?.id === "string" && value.student.id
    && (!value.centre_started_at || Number.isFinite(Date.parse(value.centre_started_at)))
    && (!value.deadline_at || Number.isFinite(Date.parse(value.deadline_at)))
    && (!value.return_at || Number.isFinite(Date.parse(value.return_at))));
}

export function part3LocationFromDevice(device, now = Date.now()) {
  if (device?.status !== "active" || !Number.isFinite(device.requestedAt)
      || device.requestedAt > now + 5000 || now - device.requestedAt > 10000
      || !Number.isFinite(device.latitude) || !Number.isFinite(device.longitude)
      || Math.abs(device.latitude) > 90 || Math.abs(device.longitude) > 180
      || !Number.isFinite(device.accuracy) || device.accuracy < 0 || device.accuracy > 100) return null;
  return { latitude: device.latitude, longitude: device.longitude, accuracy_metres: device.accuracy,
    captured_at: new Date(device.requestedAt).toISOString(),
    ...(Number.isFinite(device.speed) && device.speed >= 0 ? { speed: device.speed } : {}) };
}

export default function Part3WorkMode({ launchId }) {
  const { user } = useAuth();
  const owner = String(user?._id || user?.id || "");
  const key = [owner, user?.operating_mode, user?.admin_view_role, user?.founder_mode_subject_user_id, launchId].join(":");
  return <ScopedPart3WorkMode key={key} ownerId={owner} launchId={launchId} />;
}

function ScopedPart3WorkMode({ ownerId, launchId }) {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [routeRetry, setRouteRetry] = useState(0);
  const [bootstrap, setBootstrap] = useState(null);
  const [voice, setVoice] = useState("idle");
  const [budget, setBudget] = useState(null);
  const [parked, setParked] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [mapControlsVisible, setMapControlsVisible] = useState(false);
  const [atDestination, setAtDestination] = useState(false);
  const [nearCentreForDebrief, setNearCentreForDebrief] = useState(false);
  const [exitChoicesOpen, setExitChoicesOpen] = useState(false);
  const [text, setText] = useState("");
  const [now, setNow] = useState(Date.now());
  const current = useRef(null), alive = useRef(true), navigation = useRef(null), device = useRef(null);
  const controller = useRef(null), voiceStop = useRef(null), voiceAttempt = useRef(null);
  const actionAttempt = useRef(null), operation = useRef(null), transcript = useRef("");
  const arrival = useRef(null), arrivalAttempt = useRef(false), clockOffset = useRef(0);
  const stageViewport = useRef(null), mapControlsTimer = useRef(null);
  const actionHandler = useRef(null), commandHandler = useRef(null);
  const endpoint = `${API}/api/work-mode/part3-sessions/${encodeURIComponent(launchId)}`;

  function clearMapControlsTimer() {
    if (mapControlsTimer.current !== null) {
      window.clearTimeout(mapControlsTimer.current);
      mapControlsTimer.current = null;
    }
  }

  const stopVoice = useCallback(() => {
    voiceStop.current?.();
    controller.current?.abort();
    controller.current = null; voiceStop.current = null; voiceAttempt.current = null;
    if (alive.current) setVoice("idle");
  }, []);
  const applySession = useCallback(next => {
    if (!alive.current || !validPart3Session(next, launchId)) throw new Error("The saved Part 3 session could not be matched. Reload this stage.");
    const previous = current.current;
    if (previous && (next.student.id !== previous.student.id || next.phase_version < previous.phase_version)) return false;
    if (previous?.phase_version === next.phase_version && previous.phase !== next.phase) throw new Error("The Part 3 stage could not be confirmed.");
    if (previous?.phase_version === next.phase_version && Number(next.route_revision || 0) < Number(previous.route_revision || 0)) return false;
    if (previous?.centre_started_at && (previous.centre_started_at !== next.centre_started_at
        || previous.deadline_at !== next.deadline_at || previous.return_at !== next.return_at)) {
      throw new Error("The fixed Part 3 clock changed unexpectedly. Reload without starting another run.");
    }
    if (previous && sessionScope(previous) !== sessionScope(next)) {
      stopVoice(); transcript.current = ""; setBudget(null); setParked(false); setControlsOpen(false); setMapControlsVisible(false); clearMapControlsTimer(); setText(""); setAtDestination(false);
      if (!(previous.phase === "circuit" && next.phase === "debrief")) setNearCentreForDebrief(false);
      arrival.current = null; arrivalAttempt.current = false; operation.current = null;
    }
    const serverNow = Date.parse(next.server_time);
    if (Number.isFinite(serverNow)) clockOffset.current = serverNow - Date.now();
    current.current = next; setSession(next); setNow(Date.now());
    return true;
  }, [launchId, stopVoice]);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; clearMapControlsTimer(); controller.current?.abort(); actionAttempt.current?.abort(); void navigation.current?.stop?.(); };
  }, []);
  useEffect(() => {
    const request = new AbortController(); setLoading(true); setError("");
    if (!ownerId) { setLoading(false); setError("Sign in to open your Part 3 run."); return () => request.abort(); }
    axios.get(endpoint, { withCredentials: true, timeout: 20000, signal: request.signal }).then(({ data }) => {
      if (!request.signal.aborted) applySession(data);
    }).catch(failure => { if (!request.signal.aborted && alive.current) setError(errorMessage(failure)); })
      .finally(() => { if (!request.signal.aborted && alive.current) setLoading(false); });
    return () => request.abort();
  }, [endpoint, ownerId, reload, applySession]);
  useEffect(() => {
    if (!session?.centre_started_at || TERMINAL.has(session.phase)) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [session?.centre_started_at, session?.phase]);

  const scope = sessionScope(session);
  useLayoutEffect(() => {
    // A new saved stage starts at its heading, including after a short-screen
    // briefing was scrolled down. Keep the stage identity and Exit in view.
    if (stageViewport.current) stageViewport.current.scrollTop = 0;
  }, [scope]);
  useEffect(() => {
    if (!session || !NAV.has(session.phase)) { setBootstrap(null); return undefined; }
    const snapshot = session, request = new AbortController();
    setBootstrap({ scope, status: "loading", route: null });
    const fresh = () => !request.signal.aborted && alive.current && sessionScope(current.current) === scope;
    (async () => {
      const position = await requestPart3LaunchLocation({ signal: request.signal });
      if (!fresh()) return;
      if (position.status !== "granted") throw new Error("A fresh location is needed for this Part 3 route. Allow location, then retry when parked.");
      const { data } = await axios.post(`${endpoint}/navigation-route`, {
        expected_phase: snapshot.phase, phase_version: snapshot.phase_version, location: position.location,
      }, { withCredentials: true, timeout: 30000, signal: request.signal });
      if (!fresh()) return;
      if (data?.status !== "OK" || !validPart3Session(data.session, launchId)
          || sessionScope(data.session) !== scope) throw new Error(data?.message || "The specific Part 3 route could not be confirmed.");
      applySession(data.session);
      // Provider proximity is not a parked arrival. The live device dwell below confirms arrival.
      setAtDestination(false);
      setNearCentreForDebrief(data.session?.phase === "circuit" && data.session?.circuit_departed === true && data.near_centre_for_conversation === true);
      setBootstrap({ scope, status: "ready", route: data }); setNotice(centreConversationNotice(snapshot.phase, data));
    })().catch(failure => { if (fresh()) { setAtDestination(false); setBootstrap({ scope, status: "error", error: errorMessage(failure), route: null }); } });
    return () => request.abort();
    // The server phase is the navigation lifetime. Ordinary timing updates
    // never restart the map or reset the immutable clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, routeRetry, endpoint, launchId, applySession]);

  const freshLocation = async signal => {
    const latest = part3LocationFromDevice(device.current);
    if (latest) return latest;
    const position = await requestPart3LaunchLocation({ signal });
    if (position.status !== "granted") throw new Error("A fresh location is needed for that Part 3 action. Retry when parked.");
    return position.location;
  };
  const stopNavigation = async () => {
    if (navigation.current?.stop && await navigation.current.stop() !== true) {
      throw new Error("Navigation has not stopped yet. Retry while safely parked.");
    }
  };
  const requestAction = async (action, { explicitlyParked = false, automatic = false } = {}) => {
    const snapshot = current.current;
    if (!snapshot || TERMINAL.has(snapshot.phase) || actionAttempt.current) return false;
    const expected = sessionScope(snapshot), request = new AbortController(); actionAttempt.current = request;
    setBusy(true); setError("");
    const stillCurrent = () => alive.current && !request.signal.aborted && sessionScope(current.current) === expected;
    try {
      if (!explicitlyParked) throw new Error("Confirm you are safely parked before changing stage.");
      const location = await freshLocation(request.signal);
      if (!stillCurrent()) return false;
      if (location.speed != null && location.speed > 0.8) throw new Error("Stop safely before changing the Part 3 stage.");
      await stopNavigation();
      if (!stillCurrent()) return false;
      stopVoice();
      const identity = `${expected}:${action}`;
      if (operation.current?.key !== identity) operation.current = { key: identity, id: createPart3OperationId() };
      const { data } = await axios.post(`${endpoint}/advance`, {
        expected_phase: snapshot.phase, phase_version: snapshot.phase_version, operation_id: operation.current.id,
        action, parked: true, location,
        ...(action !== "skip" && transcript.current.trim() ? { transcript: transcript.current.trim() } : {}),
      }, { withCredentials: true, timeout: 25000, signal: request.signal });
      if (!stillCurrent()) return false;
      applySession(data); setNotice(""); return true;
    } catch (failure) {
      if (stillCurrent()) setError(errorMessage(failure));
      return false;
    } finally {
      if (actionAttempt.current === request) actionAttempt.current = null;
      if (alive.current) setBusy(false);
      if (!automatic) arrivalAttempt.current = false;
    }
  };
  actionHandler.current = requestAction;

  const requestCommand = async (command, words = transcript.current, typed = false) => {
    const snapshot = current.current;
    if (!snapshot || TERMINAL.has(snapshot.phase) || actionAttempt.current) throw new Error("Another Part 3 action is completing. Please try again.");
    const expected = sessionScope(snapshot), request = new AbortController(); actionAttempt.current = request;
    const stillCurrent = () => alive.current && !request.signal.aborted && sessionScope(current.current) === expected;
    setBusy(true); setError("");
    try {
      let resolvedCommand = command;
      if (typed) {
        const { data: prepared } = await axios.post(`${endpoint}/command-text`, {
          expected_phase: snapshot.phase, phase_version: snapshot.phase_version,
          operation_id: createPart3OperationId(), text: command, parked: Boolean(parked), preview: true,
        }, { withCredentials: true, timeout: 20000, signal: request.signal });
        if (!stillCurrent()) throw new Error("This Part 3 stage has changed.");
        if (!validPart3Session(prepared?.session, launchId)) throw new Error("The typed Part 3 request could not be matched.");
        if (prepared.action_result !== "prepared" || !COMMANDS.has(prepared.command)) {
          setNotice(prepared.message || "Which Part 3 action do you need?");
          return prepared;
        }
        resolvedCommand = prepared.command;
      }
      const transition = ["continue_part3", "finish_part3", "cancel_part3"].includes(resolvedCommand);
      if (transition && !parked) throw new Error("Confirm you are safely parked using Parked controls before changing stage.");
      // Time/clarification works even without GPS. The server rejects every
      // mutation for which a fresh location or parked confirmation is absent.
      let location = part3LocationFromDevice(device.current);
      if (resolvedCommand !== "get_time") location = await freshLocation(request.signal);
      if (!stillCurrent()) throw new Error("This Part 3 stage has changed.");
      if (transition) await stopNavigation();
      if (!stillCurrent()) throw new Error("This Part 3 stage has changed.");
      const identity = `${expected}:${typed ? "text" : "voice"}:${resolvedCommand}`;
      if (operation.current?.key !== identity) operation.current = { key: identity, id: createPart3OperationId() };
      const { data } = await axios.post(`${endpoint}/command`, {
        expected_phase: snapshot.phase, phase_version: snapshot.phase_version, operation_id: operation.current.id,
        command: resolvedCommand, parked: Boolean(parked), ...(location ? { location } : {}),
        ...(!typed && words.trim() ? { transcript: words.trim().slice(0, 24000) } : {}),
      }, { withCredentials: true, timeout: 30000, signal: request.signal });
      if (!stillCurrent()) throw new Error("This Part 3 stage has changed.");
      if (!validPart3Session(data?.session, launchId)) throw new Error("The Part 3 action receipt could not be confirmed.");
      if (data.route && NAV.has(snapshot.phase)) {
        if (typeof navigation.current?.applyPart3Route !== "function"
            || await navigation.current.applyPart3Route(data.route) !== true) {
          applySession(data.session);
          throw new Error("The route was saved but navigation has not accepted it. Retry the current Part 3 route before continuing.");
        }
      }
      applySession(data.session); setNotice(data.message || "");
      if (["applied", "read"].includes(data.action_result)) operation.current = null;
      return data;
    } catch (failure) {
      if (stillCurrent()) setError(errorMessage(failure));
      throw new Error(errorMessage(failure));
    } finally {
      if (actionAttempt.current === request) actionAttempt.current = null;
      if (alive.current) setBusy(false);
    }
  };
  commandHandler.current = requestCommand;

  const endPart3Conversation = async () => {
    const phase = current.current?.phase;
    stopVoice();
    if (!CONVERSATIONS.has(phase)) return;
    return requestAction(phase === "debrief" ? "finish" : "continue", { explicitlyParked: true });
  };

  const startVoice = async () => {
    const snapshot = current.current;
    if (!snapshot || voiceAttempt.current || TERMINAL.has(snapshot.phase)) return;
    const attempt = new AbortController(), expected = sessionScope(snapshot);
    controller.current = attempt; voiceAttempt.current = attempt; setVoice("connecting"); setError("");
    const stillCurrent = () => alive.current && !attempt.signal.aborted && sessionScope(current.current) === expected;
    try {
      if (CONVERSATIONS.has(snapshot.phase)) { await stopNavigation(); if (!stillCurrent()) return; setParked(true); }
      const result = await captureKarenPart3Conversation({ signal: attempt.signal, ownerId, session: snapshot,
        initialTranscript: transcript.current,
        onConnected: () => { if (stillCurrent()) setVoice("listening"); },
        onStopReady: fn => { if (stillCurrent()) voiceStop.current = fn; },
        onTranscript: words => { if (stillCurrent()) transcript.current = words; },
        onBudget: value => { if (stillCurrent()) setBudget(value); },
        onCommand: (command, words) => {
          if (!stillCurrent()) throw new Error("This Part 3 voice request has expired.");
          return commandHandler.current(command, words);
        },
      });
      if (stillCurrent() && result?.transcript) transcript.current = result.transcript;
    } catch (failure) { if (stillCurrent()) setError(errorMessage(failure)); }
    finally {
      if (voiceAttempt.current === attempt) { voiceAttempt.current = null; voiceStop.current = null; controller.current = null; if (alive.current) setVoice("idle"); }
    }
  };
  const startArrivalConversation = async () => {
    const phase = current.current?.phase;
    if (NAV.has(phase)) {
      const advanced = await actionHandler.current?.("arrived", { explicitlyParked: true });
      if (advanced !== true) return;
    }
    await startVoice();
  };
  const observeNavigation = useCallback(context => {
    if (sessionScope(current.current) !== scope) return;
    const snapshot = current.current, fix = context?.device;
    const location = part3LocationFromDevice(fix);
    device.current = fix;
    if (location && Number.isFinite(location.speed)) {
      const safelyStopped = location.speed <= 0.8;
      setParked(safelyStopped);
      if (!safelyStopped) { setControlsOpen(false); setMapControlsVisible(false); }
    } else { setParked(false); setControlsOpen(false); }
    if (!snapshot || !NAV.has(snapshot.phase) || actionAttempt.current || arrivalAttempt.current) return;
    if (context?.active !== true || !location
        || snapshot.phase === "circuit" && snapshot.circuit_departed !== true) {
      arrival.current = null; setAtDestination(false); return;
    }
    const last = arrival.current;
    if (last?.lastFixAt >= fix.requestedAt) return;
    // Navigation fixes use `accuracy`; the arrival contract uses the same
    // normalised `accuracy_metres` location that the server receives.
    const next = nextPart3ArrivalDwell(last, { device: location, destination: snapshot.phase_destination, nowMs: fix.requestedAt });
    arrival.current = next;
    // Move straight to the one clear arrival screen as soon as the live fix is
    // inside the saved stop. The server still verifies a parked fresh fix
    // before it changes stage; the dwell below can advance it automatically.
    const knownMoving = Number.isFinite(location.speed) && location.speed > 0.8;
    setAtDestination(Boolean(next.inside && !knownMoving));
    // iOS can omit speed at a stop. That can reveal the explicit parked
    // confirmation, but must not fabricate a parked stage-change request.
    if (next.ready && Number.isFinite(location.speed)) {
      arrivalAttempt.current = true;
      void actionHandler.current?.("arrived", { explicitlyParked: true });
    }
  }, [scope]);
  const receiveRoute = useCallback(route => {
    const snapshot = current.current;
    if (!snapshot || sessionScope(route?.session) !== sessionScope(snapshot)) return;
    if (applySession(route.session) !== true) return;
    // A route refresh does not revoke the current fix at the same saved stop.
    // Stationary phones may not emit another fix, and replaying the existing
    // one must not count towards the arrival dwell just to restore the prompt.
    const previousStop = snapshot.phase_destination, nextStop = route.session.phase_destination;
    if (previousStop?.latitude !== nextStop?.latitude || previousStop?.longitude !== nextStop?.longitude) {
      arrival.current = null; setAtDestination(false);
    }
    setError("");
    setNearCentreForDebrief(route.session?.phase === "circuit" && route.session?.circuit_departed === true && route.near_centre_for_conversation === true);
    setNotice(centreConversationNotice(snapshot.phase, route));
  }, [applySession]);
  const receiveControls = useCallback(value => {
    if (sessionScope(current.current) === scope) navigation.current = value;
  }, [scope]);
  const leaveRun = async () => { setExitChoicesOpen(false); stopVoice(); try { await stopNavigation(); navigate("/home"); } catch (failure) { setError(errorMessage(failure)); } };
  const exit = async () => {
    if (!current.current || TERMINAL.has(current.current.phase)) return leaveRun();
    setExitChoicesOpen(true);
  };
  const cancelAndExit = async () => {
    const closed = await requestAction("cancel", { explicitlyParked: true });
    if (closed) { setExitChoicesOpen(false); navigate("/home"); }
  };
  const button = "min-h-11 rounded-xl border border-slate-500 bg-slate-900/95 px-4 py-3 font-semibold text-white disabled:opacity-50";
  const primary = "min-h-12 rounded-xl bg-emerald-600 px-4 py-3 font-bold text-white disabled:opacity-50";

  if (loading || !session) return <main className="flex min-h-dvh items-center justify-center bg-slate-950 p-5 text-white" data-testid="part3-loading">
    <section className="w-full max-w-md space-y-4"><h1 className="text-2xl font-bold">Your Part 3 run</h1>
      <p role={error ? "alert" : "status"}>{error || "Opening the saved Part 3 stage…"}</p>
      {error && <button className={primary} onClick={() => setReload(v => v + 1)}>Reload Part 3</button>}
      <button className={button} onClick={exit}>Back to calendar</button></section>
  </main>;
  const navigationPhase = NAV.has(session.phase), conversation = CONVERSATIONS.has(session.phase);
  const returnDebriefScreen = session.phase === "circuit" && session.circuit_departed === true
    && (nearCentreForDebrief || atDestination);
  const arrivedForBriefing = atDestination && (session.phase === "to_pupil" || session.phase === "to_centre");
  const simpleConversationStart = conversation && voice === "idle";
  const arrivalHandoffScreen = returnDebriefScreen || arrivedForBriefing || simpleConversationStart;
  const handoffIsDebrief = returnDebriefScreen || session.phase === "debrief";
  const route = bootstrap?.scope === scope && bootstrap.status === "ready" ? bootstrap.route : null;
  const instant = now + clockOffset.current;
  const timeText = session.deadline_at ? `${minutes(Date.parse(session.deadline_at) - instant)} remaining of 40:00` : "";
  const continueLabel = session.phase === "pickup_briefing" ? "Continue to Fengate" : session.phase === "centre_briefing" ? "Start the Part 3 circuit" : "Finish this Part 3";
  const arrivalLabel = session.phase === "circuit" ? "Debrief at Fengate" : session.phase === "to_centre" ? "Briefing at Fengate" : "Briefing with your pupil";
  const conversationLabel = session.phase === "debrief" ? "debrief" : "briefing";
  const navigationOverlaysHidden = navigationPhase && Boolean(route) && !mapControlsVisible;
  const revealMapControls = () => {
    if (!navigationPhase || !route || arrivalHandoffScreen) return;
    setMapControlsVisible(true);
    clearMapControlsTimer();
    mapControlsTimer.current = window.setTimeout(() => {
      setMapControlsVisible(false);
      mapControlsTimer.current = null;
    }, 4500);
  };

  return <main data-testid="part3-work-mode" onPointerDown={revealMapControls} className="relative isolate flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-950 text-white">
    {navigationPhase && route && <section className={`absolute inset-0 ${returnDebriefScreen ? "invisible" : ""}`} data-testid="part3-navigation">
      <ContextualMap key={scope} surface="work_mode" driving autoStartNavigation routeMode="part3"
        lessonEventId={launchId} part3SessionId={launchId} part3Phase={session.phase} part3PhaseVersion={session.phase_version}
        routeGeometry={route.route_geometry} navigationInstructions={route.navigation_instructions || EMPTY}
        navigationWaypoints={route.navigation_waypoints || EMPTY} routeProvider={route.provider || "google_routes"}
        allowManualCapture={false} routeStatusOwnedByParent onPart3Route={receiveRoute}
        onNavigationContextChange={observeNavigation} onNavigationControlChange={receiveControls} />
    </section>}
    {arrivalHandoffScreen ? <section className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-5 bg-slate-950 p-6" data-testid={returnDebriefScreen ? "part3-return-debrief-screen" : "part3-arrival-briefing-screen"}>
      {["to_pupil", "pickup_briefing"].includes(session.phase) && voice === "idle" && <div className="max-w-xl space-y-3 text-center">
        <h1 className="text-3xl font-bold" data-testid="part3-arrival-briefing-title">Time for your briefing with {session.student.name || "your pupil"}</h1>
        <p className="text-lg">When you are safely parked, start the briefing.</p>
      </div>}
      {error && <p role="alert" className="w-full max-w-xl rounded-xl bg-slate-900 p-4 text-lg">{error}</p>}
      <button className={`${primary} min-h-20 w-full max-w-xl text-2xl`} disabled={busy || NAV.has(session.phase) && !atDestination || voice === "connecting"}
        onClick={NAV.has(session.phase) ? startArrivalConversation : voice === "idle" ? startVoice : endPart3Conversation}
        data-testid={handoffIsDebrief ? "part3-start-return-debrief" : "part3-start-arrival-briefing"}>
        {voice === "connecting" ? `Starting ${handoffIsDebrief ? "debrief" : "briefing"}…` : voice === "listening" ? (handoffIsDebrief ? "End debrief" : "End briefing") : NAV.has(session.phase) ? `Parked — start ${handoffIsDebrief ? "debrief" : "briefing"}` : (handoffIsDebrief ? "Start debrief" : "Start briefing")}</button>
    </section> : <div ref={stageViewport} className={`relative z-20 flex min-h-0 flex-1 flex-col ${navigationPhase ? "pointer-events-none" : "overflow-y-auto"}`}>
      {!conversation && <header data-ndg-map-overlay aria-hidden={Boolean(navigationOverlaysHidden)} inert={Boolean(navigationOverlaysHidden)} className={`flex flex-wrap items-center gap-2 bg-slate-950/90 p-3 transition-opacity duration-500 motion-reduce:transition-none ${navigationOverlaysHidden ? "pointer-events-none opacity-0" : "pointer-events-auto opacity-100"}`} style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <div className="min-w-0 flex-1"><p className="text-xs font-semibold text-emerald-300">One-off Part 3{session.exam_date ? ` · ${new Date(`${session.exam_date}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}` : ""} · Fengate</p>
          <h1 className="text-lg font-bold" data-testid="part3-phase-title">{PHASES[session.phase]}</h1><p className="text-sm">{session.student.name}</p></div>
        <button className={button} onClick={exit} data-testid="part3-exit">Exit</button>
        {session.deadline_at && <p className="w-full text-sm tabular-nums" data-testid="part3-clock">{timeText}
          {session.return_at && !TERMINAL.has(session.phase) ? ` · Return target ${minutes(Date.parse(session.return_at) - instant)}` : ""}</p>}
      </header>}
      {!conversation && (error || notice) && <p role={error ? "alert" : "status"} aria-hidden={Boolean(navigationOverlaysHidden)} inert={Boolean(navigationOverlaysHidden)} className={`mx-3 mt-2 rounded-xl bg-slate-950/95 p-3 text-sm transition-opacity duration-500 motion-reduce:transition-none ${navigationOverlaysHidden ? "pointer-events-none opacity-0" : "pointer-events-auto opacity-100"}`} data-testid="part3-notice">{error || notice}</p>}
      {navigationPhase && !route && <section className="pointer-events-auto m-auto w-full max-w-lg space-y-4 p-5" data-testid="part3-route-recovery">
        <h2 className="text-xl font-bold">{bootstrap?.status === "error" ? "Part 3 route unavailable" : "Preparing this Part 3 route"}</h2>
        <p role="status">{bootstrap?.error || "Checking fresh location and this stage’s saved road route…"}</p>
        <button className={primary} disabled={bootstrap?.status === "loading"} onClick={() => setRouteRetry(v => v + 1)}>Retry Part 3 route</button>
      </section>}
      {conversation && <section className="pointer-events-auto fixed inset-0 z-40 flex flex-col items-center justify-center gap-6 overflow-y-auto bg-slate-950 p-6 text-center" data-testid="part3-conversation">
        <h1 className="text-3xl font-bold">{session.phase === "debrief" ? "Debrief" : "Briefing"}</h1>
        <p className="max-w-xl text-xl leading-relaxed" data-testid="part3-briefing-text">
          {session.phase === "pickup_briefing"
            ? "Ask Louie how he feels. Agree the roundabout goal, what success will look like, and what help he wants. Agree when you will step in, then check that you both understand the plan."
            : session.phase === "centre_briefing"
              ? "Confirm the roundabout goal and the agreed support. Remind Louie that he can ask questions and that you will share responsibility for safety."
              : "Ask what improved, what still needs help, and what each of you noticed. Agree one next step together."}
        </p>
        {error && <p role="alert" className="max-w-xl text-lg">{error}</p>}
        {voice === "listening" && <p role="status" className="text-lg">Karen is listening. Use the button to end and continue.</p>}
        <button className={`${primary} min-h-20 w-full max-w-xl text-2xl`} disabled={busy || voice === "connecting"}
          onClick={voice === "idle" ? startVoice : endPart3Conversation} data-testid="part3-voice">
          {voice === "connecting" ? "Starting briefing…" : voice === "listening" ? (session.phase === "debrief" ? "End debrief" : "End briefing") : (session.phase === "debrief" ? "Start three-way debrief" : "Start three-way briefing")}
        </button>
      </section>}
      {TERMINAL.has(session.phase) && <section className="pointer-events-auto m-auto w-full max-w-lg space-y-4 p-5" data-testid="part3-finished">
        <h2 className="text-2xl font-bold">{session.phase === "finished" ? "This Part 3 is finished" : "This Part 3 was cancelled"}</h2>
        <p>{session.phase === "finished" ? "The saved session is closed at Fengate. There is no onward route." : "No completed practice is claimed. The run is closed."}</p>
        <button className={primary} onClick={exit}>Back to calendar</button></section>}
      {!conversation && !TERMINAL.has(session.phase) && <footer data-ndg-map-overlay aria-hidden={Boolean(navigationOverlaysHidden)} inert={Boolean(navigationOverlaysHidden)} className={`mt-auto space-y-2 bg-slate-950/90 p-3 transition-opacity duration-500 motion-reduce:transition-none ${navigationOverlaysHidden ? "pointer-events-none opacity-0" : "pointer-events-auto opacity-100"} ${navigationPhase ? "landscape:flex landscape:flex-wrap landscape:items-center landscape:gap-2 landscape:space-y-0" : ""}`} style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        {navigationPhase && <div className="flex flex-wrap gap-2 landscape:flex-1">
          {atDestination && (session.phase !== "circuit" || session.circuit_departed === true)
            && <button className={`${primary} w-full landscape:w-auto`} disabled={busy} data-testid="part3-arrived" onClick={() => requestAction("arrived", { explicitlyParked: true })}><span className="hidden landscape:inline">Parked — </span>{arrivalLabel}</button>}
          {!atDestination && <button className={button} disabled={busy} data-testid="part3-voice" aria-label={voice === "idle" ? "Voice control — route requests" : undefined} onClick={voice === "idle" ? startVoice : stopVoice}>{voice === "connecting" ? "Cancel voice connection" : voice === "listening" ? "End voice control" : "Voice control"}</button>}
        </div>}
        {(parked || atDestination) && <button className={button} data-testid="part3-parked-controls" onClick={() => { if (!controlsOpen) setParked(true); setControlsOpen(v => !v); }}>{controlsOpen ? "Close parked controls" : "Parked — more controls"}</button>}
        {controlsOpen && parked && <div className="w-full space-y-2" data-testid="part3-typed-controls">
          {navigationPhase && route && <button className={button} onClick={() => navigation.current?.retryRoute?.()} disabled={busy}>Retry route</button>}
          {navigationPhase && <form className="flex flex-wrap gap-2" onSubmit={event => { event.preventDefault(); if (text.trim()) void requestCommand(text.trim(), "", true).then(() => setText("")).catch(() => {}); }}>
            <label className="w-full text-sm" htmlFor="part3-command">Ask about this Part 3</label><input id="part3-command" data-testid="part3-command-text" value={text} onChange={e => setText(e.target.value)} maxLength={2000} disabled={busy}
              placeholder="Time left, repeat, shorten, or return to Fengate" className="min-h-11 min-w-0 flex-1 rounded-xl bg-white px-3 text-base text-slate-950" />
            <button className={primary} disabled={busy || !text.trim()} data-testid="part3-command-send">Send request</button></form>}
          <button className={button} disabled={busy} data-testid="part3-cancel" onClick={() => requestAction("cancel", { explicitlyParked: true })}>Cancel this Part 3 run</button>
        </div>}
      </footer>}
    </div>}
    {exitChoicesOpen && <section role="dialog" aria-modal="true" aria-labelledby="part3-exit-title"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/95 p-5" data-testid="part3-exit-choices">
      <div className="w-full max-w-md space-y-4 rounded-2xl border border-slate-600 bg-slate-900 p-5">
        <h2 id="part3-exit-title" className="text-xl font-bold">Leave this Part 3?</h2>
        <p>This run stays open if you leave. Cancel it to start a fresh Part 3.</p>
        {error && <p role="alert" className="text-amber-200">{error}</p>}
        <button className={button} onClick={leaveRun} disabled={busy} data-testid="part3-leave-open">Leave run open</button>
        <button className={primary} onClick={cancelAndExit} disabled={busy} data-testid="part3-cancel-and-exit">Cancel run and exit</button>
        <button className={button} onClick={() => setExitChoicesOpen(false)} disabled={busy}>Stay in this Part 3</button>
      </div>
    </section>}
  </main>;
}

